import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { use, useCallback } from 'react';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { Select } from 'app/agriculture/forms/components/Select/Select';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { useFarm } from 'app/agriculture/hooks/useFarm/useFarm';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import './FarmEditForm.scss';
import { useTimeZoneOptions } from 'app/agriculture/routes/BackOffice/FarmCreate/components/useTimeZoneOptions';

interface CreateFarmFormFields {
    name: string;
    timeZone: string;
}

export function FarmEditForm() {
    const navigate = useNavigate();
    const { farm, updateFarm } = useFarm();
    const { agricultureToastService } = use(ServicesContext);
    const form = useForm<CreateFarmFormFields>({
        mode: 'all',
        defaultValues: farm,
        shouldFocusError: true,
    });
    const timeZoneOptions = useTimeZoneOptions();

    const onSubmit: SubmitHandler<CreateFarmFormFields> = useCallback(
        (farm) => {
            updateFarm.mutate(farm, {
                onSuccess: () => {
                    void agricultureToastService.showToast({
                        title: 'Farm updated',
                        type: AgricultureToastType.SUCCESS,
                        variant: 'simple',
                    });
                    form.reset(farm);
                },
            });
        },
        [updateFarm, agricultureToastService, form],
    );

    return (
        <form className="FarmEditForm" onSubmit={form.handleSubmit(onSubmit)}>
            <SimpleGrid columns={2}>
                <Input
                    label="Farm Name"
                    {...form.register('name', {
                        required: 'Please enter a name for your farm',
                    })}
                    errorMessage={form.formState.errors.name?.message}
                />
                <Select
                    fieldName="timeZone"
                    options={timeZoneOptions.map(({ value, label }) => ({
                        id: value,
                        name: label,
                    }))}
                    noun="Time Zone"
                    formController={form}
                />
            </SimpleGrid>
            <Actions
                sticky
                primaryAction={{
                    text: 'Update Farm',
                    isLoading: updateFarm.isPending,
                    disabled: !form.formState.isDirty,
                    type: 'submit',
                }}
                backAction={{
                    text: 'Back',
                    onClick: () => {
                        navigate(-1);
                    },
                }}
            />
        </form>
    );
}
